import { useState } from 'react';
import { taxPaymentSettingsGateway } from '../index';
import type { StateTaxComponentDefinition, StateTaxEntry, StateTaxSetup } from '../types';
import { formatDate, formatDateTime, formatMoney, formatRate } from '../taxPaymentView';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

/** A draft program that takes no entries yet: it says what must be determined before it can be set up, and records nothing. */
export function NeedsReviewPanel({ name, status, description }: { name: string; status: string; description: string | null | undefined }) {
  return (
    <section className="tps-review-tab" aria-label={`${name}: ${status}`}>
      <h4>{name}</h4>
      <span className="tps-review-badge">{status}</span>
      {description && <p>{description}</p>}
      <p>No values can be recorded for this program yet. EPN does not calculate, file or pay it, and it is not part of live payroll until tax review approves it.</p>
    </section>
  );
}

interface StateTaxTabPanelProps {
  jurisdictionKey: string;
  setup: StateTaxSetup;
  componentKeys: string[];
  readOnly: boolean;
  onChanged: (next: StateTaxSetup) => void;
}

/**
 * The sections of one drawer tab. Everything comes from the state's schema, so another state needs only a schema: this holds no state-specific
 * wording. Each component is an effective-dated history table with an inline "New Entry" block; saving appends a row that the server confirms,
 * and a mistaken future-dated row can be withdrawn (anything already in force stays as history).
 */
export function StateTaxTabPanel({ jurisdictionKey, setup, componentKeys, readOnly, onChanged }: StateTaxTabPanelProps) {
  return (
    <div className="tps-sections">
      {componentKeys.map((key) => {
        const definition = setup.schema?.components.find((c) => c.key === key);
        const entries = setup.components.find((c) => c.componentKey === key)?.entries ?? [];
        if (!definition) return null;
        return <ComponentSection key={key} jurisdictionKey={jurisdictionKey} definition={definition} entries={entries} readOnly={readOnly} onChanged={onChanged} />;
      })}
    </div>
  );
}

interface SectionProps {
  jurisdictionKey: string;
  definition: StateTaxComponentDefinition;
  entries: StateTaxEntry[];
  readOnly: boolean;
  onChanged: (next: StateTaxSetup) => void;
}

function ComponentSection({ jurisdictionKey, definition, entries, readOnly, onChanged }: SectionProps) {
  const [adding, setAdding] = useState(false);
  const [value, setValue] = useState('');
  const [cap, setCap] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [errors, setErrors] = useState<{ value?: string; cap?: string; date?: string }>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingRemoval, setConfirmingRemoval] = useState<string | null>(null);
  const [removalError, setRemovalError] = useState<string | null>(null);
  const isRate = definition.kind === 'rate';
  const hasCap = Boolean(definition.capFieldLabel);
  const latest = entries.reduce<StateTaxEntry | null>((a, e) => (!a || e.recordedAtUtc > a.recordedAtUtc ? e : a), null);
  const idBase = `tps-${definition.key}`;

  const cancel = () => {
    setAdding(false);
    setValue('');
    setCap('');
    setEffectiveFrom('');
    setErrors({});
    setServerError(null);
  };

  async function save() {
    const next: { value?: string; cap?: string; date?: string } = {};
    let rate: number | null = null;
    let annualCap: number | null = null;
    if (isRate) {
      const parsed = Number(value);
      if (value.trim() === '' || Number.isNaN(parsed) || parsed < 0 || parsed > 100) next.value = `Enter the ${definition.abbreviation} rate as a percentage between 0 and 100.`;
      else if (Math.round(parsed * 10000) / 10000 !== parsed) next.value = 'Use at most four decimal places.';
      else rate = parsed;
    } else if (!value) {
      next.value = 'Choose a schedule.';
    }
    if (hasCap) {
      const parsedCap = Number(cap);
      if (cap.trim() === '' || Number.isNaN(parsedCap) || parsedCap <= 0 || parsedCap > 1_000_000) next.cap = `Enter the ${definition.abbreviation} annual cap as a dollar amount above 0.`;
      else if (Math.round(parsedCap * 100) / 100 !== parsedCap) next.cap = 'Use at most two decimal places.';
      else annualCap = parsedCap;
    }
    if (!effectiveFrom) next.date = 'Enter an effective date.';
    setErrors(next);
    setServerError(null);
    if (next.value || next.cap || next.date) return;

    setSaving(true);
    try {
      const result = await taxPaymentSettingsGateway.addStateTaxEntry(jurisdictionKey, {
        componentKey: definition.key, effectiveFrom, ...(isRate ? { ratePercent: rate } : { scheduleCode: value }), ...(hasCap ? { annualCapAmount: annualCap } : {}),
      });
      onChanged(result);
      cancel();
    } catch (err) {
      setServerError(friendlyErrorMessage(err, 'Could not save this entry.'));
    } finally {
      setSaving(false);
    }
  }

  async function remove(entry: StateTaxEntry) {
    setRemovalError(null);
    try {
      onChanged(await taxPaymentSettingsGateway.removeStateTaxEntry(jurisdictionKey, entry.id));
      setConfirmingRemoval(null);
    } catch (err) {
      setRemovalError(friendlyErrorMessage(err, 'Could not remove this entry.'));
    }
  }

  return (
    <section className="tps-section" aria-labelledby={`${idBase}-title`}>
      {definition.missingAdvisory && !entries.some((e) => e.isCurrent) && (
        <div className="tps-advisory" role="note">
          <span className="tps-ico tps-ico--warn" aria-hidden="true" />
          <span>{definition.missingAdvisory}</span>
        </div>
      )}

      <div className="tps-section__head">
        <span className="tps-badge" aria-hidden="true">{definition.abbreviation}</span>
        <div className="tps-section__title">
          <h4 id={`${idBase}-title`}>{definition.name}</h4>
          <p className="tps-muted">
            {latest ? `Last change: ${formatDateTime(latest.recordedAtUtc)} · ${latest.recordedBy}` : 'Last change: No entries yet'}
          </p>
        </div>
        {!readOnly && !adding && (
          <button type="button" className="tps-btn tps-btn--primary" onClick={() => setAdding(true)}>
            <span className="tps-ico tps-ico--plus" aria-hidden="true" /> Add Entry
          </button>
        )}
      </div>

      {adding && (
        <div className="tps-new-entry" role="group" aria-label={`New ${definition.abbreviation} entry`}>
          <div className="tps-new-entry__title">New Entry</div>
          <div className="tps-new-entry__fields">
            <div className="tps-field">
              <label htmlFor={`${idBase}-value`}>{definition.fieldLabel} <span aria-hidden="true">*</span></label>
              {isRate ? (
                <input
                  id={`${idBase}-value`} inputMode="decimal" placeholder="e.g. 3.4" value={value} autoComplete="off"
                  aria-invalid={errors.value ? true : undefined} aria-describedby={errors.value ? `${idBase}-value-error` : undefined}
                  onChange={(e) => setValue(e.target.value)}
                />
              ) : (
                <select id={`${idBase}-value`} value={value} aria-invalid={errors.value ? true : undefined} onChange={(e) => setValue(e.target.value)}>
                  <option value="">Select a schedule</option>
                  {definition.options.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
                </select>
              )}
              {errors.value && <span id={`${idBase}-value-error`} className="tps-error" role="alert">{errors.value}</span>}
            </div>
            {hasCap && (
              <div className="tps-field">
                <label htmlFor={`${idBase}-cap`}>{definition.capFieldLabel} <span aria-hidden="true">*</span></label>
                <input
                  id={`${idBase}-cap`} inputMode="decimal" placeholder="e.g. 411.91" value={cap} autoComplete="off"
                  aria-invalid={errors.cap ? true : undefined} aria-describedby={errors.cap ? `${idBase}-cap-error` : undefined}
                  onChange={(e) => setCap(e.target.value)}
                />
                {errors.cap && <span id={`${idBase}-cap-error`} className="tps-error" role="alert">{errors.cap}</span>}
              </div>
            )}
            <div className="tps-field">
              <label htmlFor={`${idBase}-date`}>Effective Date <span aria-hidden="true">*</span></label>
              <input
                id={`${idBase}-date`} type="date" value={effectiveFrom} aria-invalid={errors.date ? true : undefined}
                aria-describedby={errors.date ? `${idBase}-date-error` : undefined} onChange={(e) => setEffectiveFrom(e.target.value)}
              />
              {errors.date && <span id={`${idBase}-date-error`} className="tps-error" role="alert">{errors.date}</span>}
            </div>
            <div className="tps-new-entry__actions">
              <button type="button" className="tps-btn tps-btn--ghost" onClick={cancel} disabled={saving}>Cancel</button>
              <button type="button" className="tps-btn tps-btn--primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
          {serverError && <p className="tps-error" role="alert">{serverError}</p>}
        </div>
      )}

      {entries.length === 0 ? (
        <p className="tps-muted tps-empty">
          No entries yet.{!readOnly && !adding && <> Click <button type="button" className="tps-link" onClick={() => setAdding(true)}>Add Entry</button> to get started.</>}
        </p>
      ) : (
        <table className="tps-table" aria-label={`${definition.name} history`}>
          <thead>
            <tr>
              <th scope="col">{definition.tableLabel}</th>
              {hasCap && <th scope="col">{definition.capTableLabel ?? 'Annual Cap'}</th>}
              <th scope="col">Effective Date</th>
              <th scope="col">Added On</th>
              <th scope="col">Added By</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td>{e.ratePercent !== null ? formatRate(e.ratePercent) : e.scheduleLabel ?? e.scheduleCode}</td>
                {hasCap && <td>{e.annualCapAmount != null ? formatMoney(e.annualCapAmount) : '—'}</td>}
                <td>
                  {formatDate(e.effectiveFrom)}
                  {e.isCurrent && <span className="tps-tag"> in force</span>}
                  {e.isFuture && <span className="tps-tag tps-tag--future"> upcoming</span>}
                </td>
                <td>{formatDateTime(e.recordedAtUtc)}</td>
                <td>{e.recordedBy}</td>
                <td>
                  {confirmingRemoval === e.id ? (
                    <span className="tps-confirm" role="group" aria-label="Confirm removal">
                      <button type="button" className="tps-link tps-link--danger" onClick={() => void remove(e)}>Remove</button>
                      <button type="button" className="tps-link" onClick={() => setConfirmingRemoval(null)}>Keep</button>
                    </span>
                  ) : (
                    <button
                      type="button" className="tps-icon-btn" disabled={readOnly || !e.isFuture}
                      aria-label={`Remove ${definition.abbreviation} entry effective ${formatDate(e.effectiveFrom)}`}
                      title={e.isFuture ? 'Remove this upcoming entry' : 'Entries already in force are kept as history'}
                      onClick={() => setConfirmingRemoval(e.id)}
                    >
                      <span className="tps-ico tps-ico--trash" aria-hidden="true" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {removalError && <p className="tps-error" role="alert">{removalError}</p>}
    </section>
  );
}
